import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Users, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import {
  addEnterpriseEmployee,
  deactivateEnterpriseEmployee,
  getEnterpriseEmployees,
  reactivateEnterpriseEmployee,
} from '@/services/api.client';

const schema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: z.string().email('Enter a valid email'),
  phone: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function EnterpriseEmployeesPage() {
  const ctx = useEnterpriseContext();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: employees,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['enterprise-employees', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseEmployees(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
  });

  const invalidate = () =>
    void qc.invalidateQueries({
      queryKey: ['enterprise-employees', ctx?.propertyId, ctx?.tenantId],
    });

  const deactivateMut = useMutation({
    mutationFn: (empId: string) =>
      ctx
        ? deactivateEnterpriseEmployee(ctx.propertyId, ctx.tenantId, empId)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: () => {
      invalidate();
      setActionError(null);
    },
    onError: () => setActionError('Failed to deactivate employee. Please try again.'),
  });

  const reactivateMut = useMutation({
    mutationFn: (empId: string) =>
      ctx
        ? reactivateEnterpriseEmployee(ctx.propertyId, ctx.tenantId, empId)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: () => {
      invalidate();
      setActionError(null);
    },
    onError: () => setActionError('Failed to reactivate employee. Please try again.'),
  });

  const isMutating = deactivateMut.isPending || reactivateMut.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      setSubmitError(null);
    }
    setOpen(next);
  };

  const onSubmit = async (values: FormValues) => {
    if (!ctx) return;
    setSubmitError(null);
    try {
      await addEnterpriseEmployee(ctx.propertyId, ctx.tenantId, {
        email: values.email,
        fullName: values.fullName,
        ...(values.phone && { phone: values.phone }),
      });
      invalidate();
      handleOpenChange(false);
    } catch {
      setSubmitError('Failed to add employee. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Employees</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {actionError && (
        <p className="text-sm text-destructive text-center" role="alert">
          {actionError}
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Employees</CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load employees</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : employees?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Activation</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => {
                  // Three-way state derived from the role's is_active flag and
                  // the underlying user's account status. "Pending" tells the
                  // admin the employee was invited but hasn't set up their
                  // account yet — useful for follow-up.
                  const activationLabel = !emp.isActive
                    ? 'Deactivated'
                    : emp.status === 'pending'
                      ? 'Pending activation'
                      : 'Active';
                  const activationClass = !emp.isActive
                    ? 'badge-status badge-expired'
                    : emp.status === 'pending'
                      ? 'badge-status badge-pending'
                      : 'badge-status badge-granted';
                  const lastActive = emp.lastLoginAt
                    ? new Date(emp.lastLoginAt).toLocaleDateString('en-KE', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '—';
                  return (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.fullName}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{emp.email}</TableCell>
                      <TableCell>
                        <Badge className={activationClass}>{activationLabel}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{lastActive}</TableCell>
                      <TableCell className="text-right space-x-2">
                        {emp.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deactivateMut.mutate(emp.id)}
                            disabled={isMutating}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => reactivateMut.mutate(emp.id)}
                            disabled={isMutating}
                          >
                            Reactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center">
              <Users className="size-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No employees added yet.</p>
              <Button variant="link" size="sm" onClick={() => setOpen(true)} className="mt-1">
                Add your first employee
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="fullName">Full name</FieldLabel>
                <Input
                  id="fullName"
                  type="text"
                  aria-invalid={!!errors.fullName}
                  {...register('fullName')}
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive" role="alert">
                    {errors.fullName.message}
                  </p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  aria-invalid={!!errors.email}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-destructive" role="alert">
                    {errors.email.message}
                  </p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="phone">Phone (optional)</FieldLabel>
                <Input id="phone" type="tel" {...register('phone')} />
              </Field>
              {submitError && (
                <p className="text-sm text-destructive text-center" role="alert">
                  {submitError}
                </p>
              )}
            </FieldGroup>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Adding…' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
